<template>
    <div 
        ref="columnRef"
        class="kanban-column"
        :class="{ 'is-collapsed': isCollapsed }"
    >
        <!-- Collapsed View -->
        <div 
            v-if="isCollapsed" 
            class="collapsed-column"
            @click="() => $emit('toggle-column', groupValue)"
            :style="{ 
                background: `linear-gradient(135deg, ${color}20 0%, ${color}40 100%)`,
                borderColor: `${color}60`
            }"
        >
            <div class="collapsed-label" :style="{ color: color }">
                {{ groupLabel }}
            </div>
            <div class="collapsed-count" :style="{ backgroundColor: 'rgba(255, 255, 255, 0.9)', color: color }">
                {{ totalCount || 0 }}
            </div>
            <div class="collapsed-icon">
                <i class="material-icons" :style="{ color: `${color}80` }">chevron_right</i>
            </div>
        </div>

        <!-- Expanded View -->
        <div v-else class="expanded-column">
            <!-- Column Header -->
            <div 
                class="column-header"
                :style="{ backgroundColor: color }"
                @click="() => $emit('toggle-column', groupValue)"
            >
                <h3 class="column-title">{{ groupLabel }}</h3>
                <div class="column-actions">
                    <div class="count-badge">
                        {{ totalCount || contacts.length || 0 }}
                    </div>
                    <i class="material-icons collapse-icon">chevron_left</i>
                </div>
            </div>

            <!-- Contacts Container -->
            <div 
                ref="scrollContainerRef"
                class="contacts-container"
                :style="{ 
                    '--column-color': color,
                    '--column-color-rgb': colorRgb 
                }"
            >
                <!-- Loading State (Initial) -->
                <div v-if="!isVisible || !isLoaded" class="skeleton-loading">
                    <div v-for="i in 3" :key="`skeleton-${i}`" class="skeleton-card">
                        <div class="skeleton-header">
                            <div class="skeleton-avatar"></div>
                            <div class="skeleton-info">
                                <div class="skeleton-line short"></div>
                                <div class="skeleton-line"></div>
                            </div>
                        </div>
                        <div class="skeleton-message"></div>
                        <div class="skeleton-footer"></div>
                    </div>
                </div>

                <!-- Loading Spinner (First Load) -->
                <div v-else-if="isLoading && contacts.length === 0" class="loading-state">
                    <div class="spinner"></div>
                    <div class="loading-text">Yükleniyor...</div>
                </div>

                <!-- Empty State -->
                <div v-else-if="contacts.length === 0" class="empty-state">
                    <i class="material-icons empty-icon">inbox</i>
                    <div class="empty-text">Bu grupta contact yok</div>
                </div>

                <!-- Contact Cards -->
                <div v-else class="contacts-list">
                    <ContactCard
                        v-for="contact in contacts"
                        :key="contact._id"
                        :contact="contact"
                        :is-selected="selectedContactId === contact._id"
                        :current-time="currentTime"
                        :group-value="groupValue"
                        @click="handleContactClick"
                    />

                    <!-- Loading More -->
                    <div v-if="isLoading" class="loading-more">
                        <div class="spinner-small"></div>
                    </div>

                    <!-- Infinite Scroll Trigger -->
                    <div 
                        v-if="hasMore && !isLoading"
                        ref="scrollTriggerRef"
                        class="scroll-trigger"
                    >
                        <button 
                            class="load-more-btn"
                            @click="handleLoadMore"
                        >
                            Daha fazla yükle...
                        </button>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import ContactCard from './ContactCard.vue';
import { hexToRgb } from '../../../utils/kanbanColors';

export default {
    name: 'KanbanColumn',
    components: {
        ContactCard,
    },
    props: {
        groupValue: {
            type: [String, Boolean, Number],
            required: true,
        },
        groupLabel: {
            type: String,
            required: true,
        },
        color: {
            type: String,
            default: '#6b7280',
        },
        contacts: {
            type: Array,
            default: () => [],
        },
        isCollapsed: {
            type: Boolean,
            default: false,
        },
        isLoading: {
            type: Boolean,
            default: false,
        },
        hasMore: {
            type: Boolean,
            default: false,
        },
        totalCount: {
            type: Number,
            default: 0,
        },
        selectedContactId: {
            type: String,
            default: null,
        },
        filterChangeKey: {
            type: Number,
            default: 0,
        },
        currentTime: {
            type: Number,
            default: () => Date.now(),
        },
    },
    data() {
        return {
            isVisible: false,
            isLoaded: false,
            lastFilterKey: this.filterChangeKey,
        };
    },
    computed: {
        colorRgb() {
            const rgb = hexToRgb(this.color);
            return `${rgb.r}, ${rgb.g}, ${rgb.b}`;
        },
    },
    watch: {
        filterChangeKey(newVal, oldVal) {
            if (newVal !== oldVal) {
                this.isLoaded = false;
                this.lastFilterKey = newVal;
            }
        },
        hasMore(newVal) {
            if (newVal) {
                this.$nextTick(() => {
                    this.setupScrollObserver();
                });
            }
        },
        contacts: {
            handler() {
                this.$nextTick(() => {
                    this.setupScrollObserver();
                });
            },
            deep: false,
        },
    },
    mounted() {
        this.setupVisibilityObserver();
        this.setupScrollObserver();
    },
    beforeDestroy() {
        if (this.visibilityObserver) {
            this.visibilityObserver.disconnect();
        }
        if (this.scrollObserver) {
            this.scrollObserver.disconnect();
        }
    },
    methods: {
        setupVisibilityObserver() {
            if (!this.$refs.columnRef) return;
            
            this.visibilityObserver = new IntersectionObserver(
                (entries) => {
                    const [entry] = entries;
                    if (entry.isIntersecting) {
                        this.isVisible = true;
                        this.triggerInitialLoad();
                    } else {
                        this.isVisible = false;
                    }
                },
                { threshold: 0.1, rootMargin: '100px' }
            );
            
            this.visibilityObserver.observe(this.$refs.columnRef);
        },
        setupScrollObserver() {
            if (!this.hasMore || this.isLoading) return;
            
            if (this.scrollObserver) {
                this.scrollObserver.disconnect();
            }
            
            this.$nextTick(() => {
                const trigger = this.$refs.scrollTriggerRef;
                const container = this.$refs.scrollContainerRef;
                
                if (!trigger || !container) return;
                
                this.scrollObserver = new IntersectionObserver(
                    (entries) => {
                        const [entry] = entries;
                        if (entry.isIntersecting && this.hasMore && !this.isLoading) {
                            this.$emit('load-more');
                        }
                    },
                    { 
                        threshold: 0.5, 
                        rootMargin: '50px',
                        root: container
                    }
                );
                
                this.scrollObserver.observe(trigger);
            });
        },
        triggerInitialLoad() {
            if (this.isVisible && !this.isLoaded && !this.isLoading) {
                this.isLoaded = true;
                this.$emit('initial-load', this.groupValue);
            }
        },
        handleContactClick(contact) {
            this.$emit('contact-click', contact);
        },
        handleLoadMore() {
            if (this.hasMore && !this.isLoading) {
                this.$emit('load-more');
            }
        },
    },
};
</script>

<style lang="scss" scoped>
.kanban-column {
    flex-shrink: 0;
    height: 100%;
    
    &.is-collapsed {
        width: 36px;
    }
    
    &:not(.is-collapsed) {
        width: 320px;
    }
}

.collapsed-column {
    width: 100%;
    height: 100%;
    border: 1px solid;
    border-radius: 8px;
    cursor: pointer;
    transition: all 0.2s ease;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 8px 4px;
    
    &:hover {
        opacity: 0.9;
        transform: translateX(2px);
    }
}

.collapsed-label {
    writing-mode: vertical-rl;
    text-orientation: mixed;
    font-size: 12px;
    font-weight: 600;
    margin-bottom: 12px;
    white-space: nowrap;
}

.collapsed-count {
    padding: 4px 6px;
    border-radius: 12px;
    font-size: 11px;
    font-weight: 600;
    transform: rotate(90deg);
    margin-bottom: 12px;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.collapsed-icon {
    flex: 1;
    display: flex;
    align-items: center;
    justify-content: center;
    
    .material-icons {
        font-size: 18px;
    }
}

.expanded-column {
    height: 100%;
    display: flex;
    flex-direction: column;
    background: white;
    border: 1px solid #e5e7eb;
    border-radius: 8px;
    overflow: hidden;
}

.column-header {
    padding: 12px 16px;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: space-between;
    user-select: none;
    transition: opacity 0.2s ease;
    
    &:hover {
        opacity: 0.95;
    }
}

.column-title {
    font-size: 14px;
    font-weight: 600;
    color: white;
    margin: 0;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}

.column-actions {
    display: flex;
    align-items: center;
    gap: 8px;
}

.count-badge {
    background: rgba(255, 255, 255, 0.95);
    color: inherit;
    padding: 4px 10px;
    border-radius: 12px;
    font-size: 12px;
    font-weight: 600;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.15);
}

.collapse-icon {
    color: white;
    font-size: 20px;
}

.contacts-container {
    flex: 1;
    overflow-y: auto;
    overflow-x: hidden;
    background: linear-gradient(135deg, 
        rgba(255, 255, 255, 0.4) 0%,
        rgba(255, 255, 255, 0.15) 30%,
        rgba(var(--column-color-rgb), 0.03) 70%,
        rgba(var(--column-color-rgb), 0.08) 100%
    );
    
    &::-webkit-scrollbar {
        width: 6px;
    }
    
    &::-webkit-scrollbar-track {
        background: #f9fafb;
    }
    
    &::-webkit-scrollbar-thumb {
        background: #d1d5db;
        border-radius: 3px;
        
        &:hover {
            background: #9ca3af;
        }
    }
}

.skeleton-loading {
    padding: 12px;
    display: flex;
    flex-direction: column;
    gap: 12px;
}

.skeleton-card {
    background: white;
    border: 1px solid #e5e7eb;
    border-radius: 8px;
    padding: 12px;
}

.skeleton-header {
    display: flex;
    gap: 10px;
    margin-bottom: 8px;
}

.skeleton-avatar {
    width: 40px;
    height: 40px;
    border-radius: 50%;
    background: linear-gradient(90deg, #f3f4f6 25%, #e5e7eb 50%, #f3f4f6 75%);
    background-size: 200% 100%;
    animation: skeleton-loading 1.5s infinite;
}

.skeleton-info {
    flex: 1;
    display: flex;
    flex-direction: column;
    gap: 6px;
}

.skeleton-line {
    height: 12px;
    background: linear-gradient(90deg, #f3f4f6 25%, #e5e7eb 50%, #f3f4f6 75%);
    background-size: 200% 100%;
    animation: skeleton-loading 1.5s infinite;
    border-radius: 4px;
    
    &.short {
        width: 60%;
    }
}

.skeleton-message {
    height: 32px;
    background: linear-gradient(90deg, #f3f4f6 25%, #e5e7eb 50%, #f3f4f6 75%);
    background-size: 200% 100%;
    animation: skeleton-loading 1.5s infinite;
    border-radius: 4px;
    margin-bottom: 8px;
}

.skeleton-footer {
    height: 20px;
    width: 40%;
    background: linear-gradient(90deg, #f3f4f6 25%, #e5e7eb 50%, #f3f4f6 75%);
    background-size: 200% 100%;
    animation: skeleton-loading 1.5s infinite;
    border-radius: 4px;
}

@keyframes skeleton-loading {
    0% {
        background-position: 200% 0;
    }
    100% {
        background-position: -200% 0;
    }
}

.loading-state,
.empty-state {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 40px 20px;
    text-align: center;
}

.spinner {
    width: 32px;
    height: 32px;
    border: 3px solid #e5e7eb;
    border-top-color: #3b82f6;
    border-radius: 50%;
    animation: spin 0.8s linear infinite;
}

.loading-text {
    margin-top: 12px;
    color: #6b7280;
    font-size: 13px;
}

.empty-icon {
    font-size: 48px;
    color: #d1d5db;
    margin-bottom: 8px;
}

.empty-text {
    color: #9ca3af;
    font-size: 13px;
}

.contacts-list {
    padding: 12px;
    display: flex;
    flex-direction: column;
    gap: 12px;
}

.loading-more {
    display: flex;
    justify-content: center;
    padding: 16px;
}

.spinner-small {
    width: 24px;
    height: 24px;
    border: 2px solid #e5e7eb;
    border-top-color: #3b82f6;
    border-radius: 50%;
    animation: spin 0.8s linear infinite;
}

@keyframes spin {
    to {
        transform: rotate(360deg);
    }
}

.scroll-trigger {
    padding: 12px;
}

.load-more-btn {
    width: 100%;
    padding: 10px 16px;
    border: 1px solid #d1d5db;
    border-radius: 8px;
    background: white;
    color: #3b82f6;
    font-size: 13px;
    font-weight: 500;
    cursor: pointer;
    transition: all 0.2s ease;
    
    &:hover:not(:disabled) {
        background: #eff6ff;
        border-color: #3b82f6;
    }
    
    &:disabled {
        opacity: 0.5;
        cursor: not-allowed;
    }
}
</style>

